import { createAsyncThunk } from '@reduxjs/toolkit';
import OrganizationAnalyticsService from '../../../api/services/OrganizationAnalyticsService';
import { createOrganizationAnalyticsRequestKey, ORGANIZATION_ANALYTICS_CACHE_TTL_MS } from './requestKey';

const isCacheFresh = fetchedAt => {
  return typeof fetchedAt === 'number' && Date.now() - fetchedAt < ORGANIZATION_ANALYTICS_CACHE_TTL_MS;
};

const createConditionalAnalyticsThunk = (type, stateKey, requestFn) => {
  return createAsyncThunk(type, async requestData => {
    return await requestFn(requestData);
  }, {
    condition: (requestData, { getState }) => {
      const requestKey = createOrganizationAnalyticsRequestKey(requestData);
      const fetchInfo = getState().organizationAnalyticsInfo[stateKey];

      if (!fetchInfo) {
        return true;
      }

      if (fetchInfo.pending && fetchInfo.pendingRequestKey === requestKey) {
        return false;
      }

      if (fetchInfo.loaded && fetchInfo.requestKey === requestKey && isCacheFresh(fetchInfo.fetchedAt)) {
        return false;
      }

      return true;
    },
  });
};

export const fetchInfluencerEarningsByContentTypeByTime = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchInfluencerEarningsByContentTypeByTime',
  'fetchInfluencerEarningsByContentTypeByTimeInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchInfluencerEarningsByContentTypeByTime(requestData);
  },
);

export const fetchInfluencerEarningsByContentTypeByInfluencer = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchInfluencerEarningsByContentTypeByInfluencer',
  'fetchInfluencerEarningsByContentTypeByInfluencerInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchInfluencerEarningsByContentTypeByInfluencer(requestData);
  },
);

export const fetchInfluencerEarningsByMessageTypeByTime = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchInfluencerEarningsByMessageTypeByTime',
  'fetchInfluencerEarningsByMessageTypeByTimeInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchInfluencerEarningsByMessageTypeByTime(requestData);
  },
);

export const fetchInfluencerEarningsByMessageTypeByInfluencer = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchInfluencerEarningsByMessageTypeByInfluencer',
  'fetchInfluencerEarningsByMessageTypeByInfluencerInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchInfluencerEarningsByMessageTypeByInfluencer(requestData);
  },
);

export const fetchInfluencerNewUsersByTime = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchInfluencerNewUsersByTime',
  'fetchInfluencerNewUsersByTimeInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchInfluencerNewUsersByTime(requestData);
  },
);

export const fetchInfluencerUserInfluencersByTime = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchInfluencerUserInfluencersByTime',
  'fetchInfluencerUserInfluencersByTimeInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchInfluencerUserInfluencersByTime(requestData);
  },
);

export const fetchInfluencerUserInfluencersByInfluencer = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchInfluencerUserInfluencersByInfluencer',
  'fetchInfluencerUserInfluencersByInfluencerInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchInfluencerUserInfluencersByInfluencer(requestData);
  },
);

export const fetchInfluencerTableData = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchInfluencerTableData',
  'fetchInfluencerTableDataInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchInfluencerTableData(requestData);
  },
);

export const fetchChatterTotalEarningsByTime = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterTotalEarningsByTime',
  'fetchChatterTotalEarningsByTimeInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterTotalEarningsByTime(requestData);
  },
);

export const fetchChatterTotalEarningsByChatter = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterTotalEarningsByChatter',
  'fetchChatterTotalEarningsByChatterInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterTotalEarningsByChatter(requestData);
  },
);

export const fetchChatterTotalMessagesByChatter = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterTotalMessagesByChatter',
  'fetchChatterTotalMessagesByChatterInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterTotalMessagesByChatter(requestData);
  },
);

export const fetchChatterTotalMessagesByTime = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterTotalMessagesByTime',
  'fetchChatterTotalMessagesByTimeInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterTotalMessagesByTime(requestData);
  },
);

export const fetchChatterTotalChatsByChatter = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterTotalChatsByChatter',
  'fetchChatterTotalChatsByChatterInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterTotalChatsByChatter(requestData);
  },
);

export const fetchChatterTotalChatsByTime = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterTotalChatsByTime',
  'fetchChatterTotalChatsByTimeInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterTotalChatsByTime(requestData);
  },
);

export const fetchChatterAverageResponseTimeByChatter = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterAverageResponseTimeByChatter',
  'fetchChatterAverageResponseTimeByChatterInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterAverageResponseTimeByChatter(requestData);
  },
);

export const fetchChatterAverageResponseTimeByTime = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterAverageResponseTimeByTime',
  'fetchChatterAverageResponseTimeByTimeInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterAverageResponseTimeByTime(requestData);
  },
);

export const fetchChatterManualVsAiByTime = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterManualVsAiByTime',
  'fetchChatterManualVsAiByTimeInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterManualVsAiByTime(requestData);
  },
);

export const fetchChatterManualVsAiByChatter = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterManualVsAiByChatter',
  'fetchChatterManualVsAiByChatterInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterManualVsAiByChatter(requestData);
  },
);

export const fetchChatterPpvRateByTime = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterPpvRateByTime',
  'fetchChatterPpvRateByTimeInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterPpvRateByTime(requestData);
  },
);

export const fetchChatterPpvRateByChatter = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterPpvRateByChatter',
  'fetchChatterPpvRateByChatterInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterPpvRateByChatter(requestData);
  },
);

export const fetchChatterTableData = createConditionalAnalyticsThunk(
  'organizationAnalytics/fetchChatterTableData',
  'fetchChatterTableDataInfo',
  async requestData => {
    return await OrganizationAnalyticsService.fetchChatterTableData(requestData);
  },
);
